import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { usersApi, type User } from '../api/users';
import { getApiErrorMessage } from '../utils/errorHandling';
import PageHero from '../components/ui/PageHero';

export default function Profile() {
  const { user, login, token } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await usersApi.getMe();
      setProfile(data);
      setFirstName(data.firstName);
      setLastName(data.lastName);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load profile'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await usersApi.updateMe({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(password ? { password, currentPassword: currentPassword || undefined } : {}),
      });
      setProfile(updated);
      setPassword('');
      setCurrentPassword('');
      if (token) {
        login(token, {
          id: updated.id,
          email: updated.email,
          firstName: updated.firstName,
          lastName: updated.lastName,
          role: updated.role,
        });
      }
      toast.success('Profile updated');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const formatWhen = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  if (loading) {
    return <p className="p-8 text-slate-500">Loading profile…</p>;
  }

  return (
    <div className="space-y-6">
      <PageHero
        title="My profile"
        subtitle="Your demo account — you can edit your own details only, not other users."
      />

      <div className="glass-panel max-w-xl rounded-2xl border border-slate-200/60 p-6">
        <dl className="mb-6 grid gap-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Email</dt>
            <dd className="font-medium text-slate-900">{profile?.email || user?.email}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Role</dt>
            <dd className="font-medium capitalize text-slate-900">{profile?.role || user?.role}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Last sign-in</dt>
            <dd className="font-medium text-slate-900">{formatWhen(profile?.lastLoginAt)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Member since</dt>
            <dd className="font-medium text-slate-900">{formatWhen(profile?.createdAt)}</dd>
          </div>
        </dl>

        <form className="space-y-4" onSubmit={save}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">First name</label>
              <input
                className="input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Last name</label>
              <input
                className="input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <p className="mb-3 text-sm font-medium text-slate-700">Change password (optional)</p>
            {profile?.authProvider === 'google' && (
              <p className="mb-2 text-xs text-slate-500">
                Google sign-in account — leave current password blank to set your first local password.
              </p>
            )}
            <div className="space-y-3">
              <input
                className="input"
                type="password"
                placeholder="New password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
              />
              <input
                className="input"
                type="password"
                placeholder="Current password (required if you already use email/password)"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      </div>
    </div>
  );
}
